import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { PaddleProvider } from './context/PaddleContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeModeContext';
import SuccessPage from './features/paddle/SuccessPage';
import CanceledPage from './features/paddle/CanceledPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import Dashboard from './features/dashboard/Dashboard';
import LoginPage from './features/auth/LoginPage';
import { CloudAuthCallbackPage } from './features/auth/cloudAuth';
import SsoBootHandoff from './features/auth/cloudAuth/SsoBootHandoff';
import ProtectedRoute from './features/auth/ProtectedRoute';
import SessionTimeoutHandler from './features/auth/SessionTimeoutHandler';
import WebsiteLayout from './features/website/components/WebsiteLayout';
import { websiteNestedRoutes } from './features/website/config/pages';
import { getConfig } from './config/appConfig';
import { getAppTheme } from './theme/buildTheme';
import ScrollToTop from './components/common/ScrollToTop';
import { ssoAuthCallbackPath } from './config/environments';
import CanonicalSpaHostGate from './features/auth/CanonicalSpaHostGate';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: getConfig('api.retryAttempts'),
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  },
});

const globalStyles = `
  @keyframes spin { to { transform: rotate(360deg); } }
`;

function AppContent() {
  const { mode, isDark } = useThemeMode();
  const muiTheme = useMemo(() => getAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <style>{globalStyles}</style>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <SubscriptionProvider>
              <PaddleProvider>
                <Router
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  {/* ***** Code Start BH15092026_1445 - Canonical SPA host gate */}
                  <CanonicalSpaHostGate>
                  <div className="App" data-theme={mode}>
                    <ScrollToTop />
                    <SessionTimeoutHandler />
                    <SsoBootHandoff />
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path={ssoAuthCallbackPath()} element={<CloudAuthCallbackPage />} />
                      <Route path="/" element={<WebsiteLayout />}>
                        {websiteNestedRoutes.map(({ id, path, index, Component }) =>
                          index ? (
                            <Route key={id} index element={<Component />} />
                          ) : (
                            <Route key={id} path={path} element={<Component />} />
                          )
                        )}
                      </Route>
                      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/subscription-dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      {/* ***** Code Start BH10092026_1400 - /upgrade uses Dashboard shell (avatar: Profile/Logout + sidebar) */}
                      <Route path="/upgrade" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      {/* ***** Code End BH10092026_1400 */}
                      <Route path="/billing" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/usage" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/addons" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/plans" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/status" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/success" element={<ProtectedRoute><SuccessPage /></ProtectedRoute>} />
                      <Route path="/canceled" element={<ProtectedRoute><CanceledPage /></ProtectedRoute>} />
                    </Routes>

                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 5000,
                        style: {
                          background: isDark ? '#1e293b' : '#0f172a',
                          color: '#f8fafc',
                          borderRadius: '10px',
                          fontSize: '0.875rem',
                          border: isDark ? '1px solid rgba(148,163,184,0.2)' : 'none',
                        },
                        success: {
                          duration: 3000,
                          iconTheme: {
                            primary: getConfig('ui.theme.successColor'),
                            secondary: '#fff',
                          },
                        },
                        error: {
                          duration: 5000,
                          iconTheme: {
                            primary: getConfig('ui.theme.errorColor'),
                            secondary: '#fff',
                          },
                        },
                      }}
                    />
                  </div>
                  </CanonicalSpaHostGate>
                  {/* ***** Code End BH15092026_1445 */}
                </Router>
              </PaddleProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <AppContent />
    </ThemeModeProvider>
  );
}

export default App;
